/* eslint-disable max-lines */
import {
  ChevronLeftIcon,
  NoSymbolIcon,
  PencilSquareIcon
} from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DeleteModal from "../../../components/ui/DeleteModal";
import Status from "../../../components/ui/Status";
import useFetch from "../../../hooks/useFetch";
import {
  cancelBroadcast,
  getBroadcastById
} from "../../../services/BroadcastNotification.service";
import {
  appLanguageLabel,
  appRouteLabel,
  BroadcastStatus,
  broadcastStatusColor,
  broadcastStatusLabel
} from "../../../utils/esim/broadcastStatus";
import {
  formatCetDateTime,
  formatUtcDateTime
} from "../../../utils/esim/format";

const listCrumbs = ["Esim", "Njoftimet"];

const StatCard: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value
}) => (
  <div className="bg-white rounded-lg border p-4">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-2xl font-semibold tabular-nums">{value}</p>
  </div>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value
}) => (
  <div className="flex justify-between gap-4 py-2 border-b last:border-b-0 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);

const BroadcastDetail: React.FC = () => {
  const { id } = useParams();

  const { response, refetch } = useFetch(getBroadcastById, {
    initRequest: true,
    params: Number(id)
  });

  const data = response?.data;

  // The send and the receipt check run in the background — refresh the numbers
  // while they are still in progress.
  useEffect(() => {
    if (!data) return;
    const inProgress =
      data.status === BroadcastStatus.Scheduled ||
      data.status === BroadcastStatus.Sending ||
      (data.status === BroadcastStatus.Completed && !data.receiptsChecked);
    if (!inProgress) return;

    const interval = setInterval(() => refetch(), 10000);
    return () => clearInterval(interval);
  }, [data?.status, data?.receiptsChecked]);

  if (!data) return null;

  const canEdit = data.status === BroadcastStatus.Scheduled;
  const canCancel =
    data.status === BroadcastStatus.Scheduled ||
    data.status === BroadcastStatus.Sending;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <Link
          className="flex items-center text-sm font-semibold text-primary"
          to="/esim/broadcasts"
          state={listCrumbs}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-2" />
          <span>Kthehu tek lista</span>
        </Link>
        <div className="flex items-center gap-3">
          {canEdit && (
            <Link
              to={`/esim/broadcasts/edit/${data.id}`}
              state={{ crumbs: listCrumbs }}
              className="flex items-center gap-1 text-sm font-semibold text-primary"
            >
              <PencilSquareIcon className="w-4 h-4" />
              Modifiko
            </Link>
          )}
          {canCancel && (
            <DeleteModal
              handleDelete={() =>
                cancelBroadcast(data.id).then((res) => {
                  refetch();
                  return res;
                })
              }
              params={data.id}
              title="Konfirmo anulimin"
              paragraph="Njoftimi nuk do të dërgohet. Nëse dërgimi ka filluar, ai ndalet te grupi i radhës."
              successMessage="Njoftimi u anulua"
              errorMessage="Anulimi dështoi"
              loadingMessage="Duke anuluar..."
              indicator={
                <span className="flex items-center gap-1 text-sm font-semibold text-warning cursor-pointer">
                  <NoSymbolIcon className="w-4 h-4" />
                  Anulo dërgimin
                </span>
              }
            />
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-xl font-medium">{data.name}</h1>
          <Status
            type={broadcastStatusColor(data.status)}
            name={broadcastStatusLabel(data.status)}
          />
        </div>
        {data.errorMessage && (
          <p className="mt-2 text-sm text-warning">
            Gabimi i fundit: {data.errorMessage}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard
          label={
            data.status === BroadcastStatus.Scheduled
              ? "Pajisjet e synuara (vlerësim)"
              : "Pajisjet e synuara"
          }
          value={data.totalDevices}
        />
        <StatCard label="Të dërguara" value={data.sentCount} />
        <StatCard
          label="Të pranuara nga pajisjet"
          value={
            data.receiptsChecked ? (
              data.deliveredCount
            ) : (
              <span
                className="text-base text-gray-400"
                title="Konfirmimet e pranimit merren rreth 15 minuta pas dërgimit"
              >
                Në pritje...
              </span>
            )
          }
        />
        <StatCard label="Të dështuara" value={data.failedCount} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-base font-medium mb-2">Detajet e dërgimit</h2>
          <InfoRow label="Krijuar më" value={formatCetDateTime(data.insertDate)} />
          <InfoRow
            label="Planifikuar për"
            value={
              data.scheduledAtUtc ? formatUtcDateTime(data.scheduledAtUtc) : "Menjëherë"
            }
          />
          <InfoRow label="Filloi" value={formatUtcDateTime(data.startedAtUtc)} />
          <InfoRow label="Përfundoi" value={formatUtcDateTime(data.completedAtUtc)} />
          {data.targetUserEmails.length > 0 ? (
            <InfoRow
              label="Marrësit"
              value={
                <span className="break-all">
                  {data.targetUserEmails.join(", ")}
                </span>
              }
            />
          ) : (
            <>
              <InfoRow
                label="Platforma"
                value={
                  data.targetPlatform
                    ? data.targetPlatform === "ios"
                      ? "Vetëm iOS"
                      : "Vetëm Android"
                    : "Të gjitha"
                }
              />
              <InfoRow
                label="Gjuhët e synuara"
                value={
                  data.targetLanguages.length
                    ? data.targetLanguages.map(appLanguageLabel).join(", ")
                    : "Të gjitha"
                }
              />
              <InfoRow
                label="Versioni minimal"
                value={data.minAppVersion || "Pa kufizim"}
              />
            </>
          )}
          <InfoRow
            label="Kur preket, hapet"
            value={appRouteLabel(data.targetRoute)}
          />
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-base font-medium mb-2">Mesazhet</h2>
          {data.messages.map((message) => (
            <div
              key={message.id}
              className="rounded-lg border bg-gray-50 p-3 mb-2"
            >
              <p className="text-xs text-gray-500 mb-1">
                {message.isDefault
                  ? "Mesazhi kryesor"
                  : appLanguageLabel(message.applicationLanguage)}
              </p>
              <p className="text-sm font-semibold">{message.title}</p>
              <p className="text-sm text-gray-700">{message.body}</p>
            </div>
          ))}
        </div>
      </div>

      {data.errorBreakdown.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-base font-medium mb-2">
            Arsyet kryesore të dështimeve
          </h2>
          {data.errorBreakdown.map((group, i) => (
            <div
              key={i}
              className="flex justify-between gap-4 py-2 border-b last:border-b-0 text-sm"
            >
              <span className="text-gray-700 break-all">{group.error}</span>
              <span className="font-semibold tabular-nums">{group.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BroadcastDetail;
